import React, { useState, useEffect } from 'react';
import api from '@/api';
import { FormField } from './FormField';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Plus, Edit, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function Guests() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = async (q = '') => {
    setLoading(true);
    try {
      const res = await api.get(`/guests?search=${q}`);
      setGuests(res.data);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]);

  const openCreate = () => { setEditing(null); setForm({ name: '', email: '', phone: '', notes: '' }); setPanelOpen(true); };
  const openEdit = (g) => { setEditing(g.id); setForm({ name: g.name, email: g.email || '', phone: g.phone || '', notes: g.notes || '' }); setPanelOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error('Nome obbligatorio'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/guests/${editing}`, form);
        toast.success('Ospite aggiornato');
      } else {
        await api.post('/guests', form);
        toast.success('Ospite creato');
      }
      setPanelOpen(false);
      load(search);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm('Eliminare questo ospite?')) return;
    await api.delete(`/guests/${id}`);
    load(search);
    toast.success('Ospite eliminato');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Ospiti</h2>
        </div>
        <button onClick={openCreate} className="btn-gold flex items-center gap-2" data-testid="new-guest-btn">
          <Plus size={14} /> Nuovo Ospite
        </button>
      </div>
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--lux-text-muted-3)]" />
        <input className="input-dark pl-9" placeholder="Cerca per nome o email..." value={search} onChange={e => setSearch(e.target.value)} data-testid="guest-search-input" />
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div> : (
        <div className="card-dark rounded-lg overflow-hidden">
          {guests.length === 0 ? (
            <div className="p-12 text-center"><p className="text-[var(--lux-text-muted-3)] text-sm">Nessun ospite trovato.</p></div>
          ) : (
            <table className="w-full table-dark">
              <thead><tr>
                <th className="text-left">Nome</th>
                <th className="text-left hidden sm:table-cell">Email</th>
                <th className="text-left hidden md:table-cell">Telefono</th>
                <th />
              </tr></thead>
              <tbody>
                {guests.map(g => (
                  <tr key={g.id}>
                    <td className="text-white">{g.name}</td>
                    <td className="hidden sm:table-cell text-[var(--lux-text-sec)] text-xs">{g.email || '—'}</td>
                    <td className="hidden md:table-cell text-[var(--lux-text-muted-2)] text-xs">{g.phone || '—'}</td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(g)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-white"><Edit size={13} /></button>
                        <button onClick={() => remove(g.id)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-red-400"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-sm overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl font-light text-white">{editing ? 'Modifica' : 'Nuovo'} Ospite</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <FormField label="Nome *" name="name" form={form} setForm={setForm} testId="guest-field-name" />
            <FormField label="Email" name="email" form={form} setForm={setForm} testId="guest-field-email" />
            <FormField label="Telefono" name="phone" form={form} setForm={setForm} testId="guest-field-phone" />
            <FormField label="Note" name="notes" rows={3} form={form} setForm={setForm} testId="guest-field-notes" />
            <div className="flex gap-3 pt-4 border-t border-[#222]">
              <button onClick={save} disabled={saving} className="btn-gold flex-1 flex items-center justify-center gap-2">
                {saving && <Loader2 size={13} className="animate-spin" />}
                Salva
              </button>
              <button onClick={() => setPanelOpen(false)} className="btn-outline-gold flex-1">Annulla</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
